import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";

import {
  legalDocumentEditFormSchema,
  legalDocumentFormSchema,
  TLegalDocumentEditFormValues,
  TLegalDocumentFormValues,
} from "@/api/legal/legal.types";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/forms";
import { Button } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/Checkbox";
import { Input } from "@/components/ui/Input";
import { Typography } from "@/components/ui/Typography";

import { DOCUMENT_TYPE_LABELS, DOCUMENT_TYPE_OPTIONS, toDateInputValue } from "./constants";
import { LegalRichTextEditor } from "./LegalRichTextEditor";

export const LEGAL_DOCUMENT_EDIT_FORM_ID = "legal-document-edit-form";
export const LEGAL_DOCUMENT_CREATE_FORM_ID = "legal-document-create-form";

type TLegalDocumentFormBaseProps = {
  defaultValues?: Partial<TLegalDocumentFormValues> | Partial<TLegalDocumentEditFormValues>;
  onCancel?: () => void;
  isSubmitting?: boolean;
  /** Stretch editor to fill column height (document detail layout). */
  fillEditor?: boolean;
  /** HTML form id for external submit buttons (detail page sticky save). */
  formId?: string;
  readOnlyFields?: {
    document_type?: string;
    version?: string;
  };
  onPreviewChange?: (values: {
    title: string;
    content: string;
    version?: string;
    effective_at?: string;
  }) => void;
};

type TLegalDocumentFormProps =
  | (TLegalDocumentFormBaseProps & {
      mode: "create";
      onSubmit: (values: TLegalDocumentFormValues) => Promise<void>;
    })
  | (TLegalDocumentFormBaseProps & {
      mode: "edit";
      onSubmit: (values: TLegalDocumentEditFormValues) => Promise<void>;
    });

export function LegalDocumentForm({
  mode,
  defaultValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
  fillEditor = false,
  formId,
  readOnlyFields,
  onPreviewChange,
}: TLegalDocumentFormProps) {
  const isCreate = mode === "create";

  const form = useForm<TLegalDocumentFormValues | TLegalDocumentEditFormValues>({
    resolver: zodResolver(isCreate ? legalDocumentFormSchema : legalDocumentEditFormSchema),
    defaultValues: isCreate
      ? {
          document_type: "terms_of_service",
          version: "",
          title: "",
          content: "",
          effective_at: new Date().toISOString().slice(0, 10),
          is_active: false,
          ...defaultValues,
        }
      : {
          title: "",
          content: "",
          effective_at: new Date().toISOString().slice(0, 10),
          ...defaultValues,
        },
  });

  const watchedTitle = useWatch({ control: form.control, name: "title" });
  const watchedContent = useWatch({ control: form.control, name: "content" });
  const watchedVersion = useWatch({ control: form.control, name: "version", disabled: !isCreate });
  const watchedEffectiveAt = useWatch({ control: form.control, name: "effective_at" });

  useEffect(() => {
    if (!onPreviewChange) return;
    onPreviewChange({
      title: typeof watchedTitle === "string" ? watchedTitle : "",
      content: typeof watchedContent === "string" ? watchedContent : "",
      version: typeof watchedVersion === "string" ? watchedVersion : undefined,
      effective_at: typeof watchedEffectiveAt === "string" ? watchedEffectiveAt : undefined,
    });
  }, [watchedTitle, watchedContent, watchedVersion, watchedEffectiveAt, onPreviewChange]);

  const handleSubmit = form.handleSubmit(async (values) => {
    await onSubmit(values as TLegalDocumentFormValues & TLegalDocumentEditFormValues);
  });

  const showInlineSave = !fillEditor || !formId;

  const actionButtons = (
    <div className="flex flex-wrap items-center gap-3">
      <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
        {isCreate ? "Create version" : "Save changes"}
      </Button>
      {onCancel ? (
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
      ) : null}
    </div>
  );

  const readOnlyMetadata = !isCreate && readOnlyFields ? (
    <div className="shrink-0 space-y-3 border-b border-border pb-4 text-sm">
      <div className="space-y-1">
        <Typography variant="p" affects="small" className="text-muted-foreground">
          Document type
        </Typography>
        <Typography variant="p" className="font-medium">
          {readOnlyFields.document_type
            ? DOCUMENT_TYPE_LABELS[readOnlyFields.document_type as keyof typeof DOCUMENT_TYPE_LABELS] ??
              readOnlyFields.document_type
            : "—"}
        </Typography>
      </div>
      <div className="space-y-1">
        <Typography variant="p" affects="small" className="text-muted-foreground">Version</Typography>
        <Typography variant="p" className="font-medium">{readOnlyFields.version ?? "—"}</Typography>
      </div>
    </div>
  ) : null;

  const metadataSection = (
    <>
      {isCreate ? (
        <div className={fillEditor ? "space-y-4" : "grid gap-4 md:grid-cols-2"}>
          <FormField
            control={form.control}
            name="document_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Document type</FormLabel>
                <FormControl>
                  <select
                    className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={field.value as string}
                    onChange={field.onChange}
                  >
                    {DOCUMENT_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="version"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Version</FormLabel>
                <FormControl>
                  <Input placeholder="1.0.0" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      ) : (
        readOnlyMetadata
      )}

      <div className={fillEditor ? "space-y-4" : "grid gap-4 sm:grid-cols-2"}>
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title</FormLabel>
              <FormControl>
                <Input placeholder="Document title" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="effective_at"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Effective date</FormLabel>
              <FormControl>
                <Input
                  type="date"
                  value={toDateInputValue(field.value)}
                  onChange={(event) => field.onChange(event.target.value)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {isCreate ? (
        <FormField
          control={form.control}
          name="is_active"
          render={({ field }) => (
            <FormItem className="flex items-center gap-3 space-y-0">
              <FormControl>
                <Checkbox checked={field.value ?? false} onCheckedChange={field.onChange} />
              </FormControl>
              <div className="space-y-0.5">
                <FormLabel className="font-medium">Activate on create</FormLabel>
                <Typography variant="p" affects="muted" className="text-xs">
                  Deactivates other versions of the same document type.
                </Typography>
              </div>
            </FormItem>
          )}
        />
      ) : null}
    </>
  );

  const contentField = (
    <FormField
      control={form.control}
      name="content"
      render={({ field }) => (
        <FormItem className={fillEditor ? "flex min-h-0 flex-1 flex-col space-y-2" : undefined}>
          <FormLabel className={fillEditor ? "shrink-0" : undefined}>Content</FormLabel>
          <FormControl className={fillEditor ? "flex min-h-0 flex-1 flex-col" : undefined}>
            <LegalRichTextEditor
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              disabled={isSubmitting}
              fillHeight={fillEditor}
              className={fillEditor ? "min-h-0 flex-1" : undefined}
            />
          </FormControl>
          {!fillEditor ? (
            <Typography variant="p" affects="muted" className="text-xs">
              Format content visually. Published pages match this layout.
            </Typography>
          ) : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <Form {...form}>
      <form
        id={formId}
        onSubmit={(event) => void handleSubmit(event)}
        className={
          fillEditor
            ? "flex min-h-0 flex-1 flex-col gap-5 lg:grid lg:grid-cols-[minmax(11rem,13.5rem)_minmax(0,1fr)] lg:items-stretch lg:gap-0"
            : "space-y-6"
        }
      >
        {fillEditor ? (
          <>
            <div className="flex min-h-0 flex-col gap-4 lg:min-h-0 lg:pr-5">
              {metadataSection}
              {showInlineSave ? actionButtons : null}
            </div>
            <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:border-l lg:border-border lg:pl-6">
              {contentField}
            </div>
          </>
        ) : (
          <>
            {metadataSection}
            {contentField}
            {actionButtons}
          </>
        )}
      </form>
    </Form>
  );
}
